import React, { useState, useEffect, useMemo } from 'react';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import Loader from '../common/Loader/Loader';
import HeaderElements from '../navigation/components/HeaderElements';
import 'react-grid-layout/css/styles.css';
import './SummaryDashboard.scss';
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { removeFilter } from '../well-filters/WellFilterSlice';
import {
  fetchAlarmsData,
  fetchRodpumpclassification,
  fetchFieldProduction,
  fetchFilteredWellSummary,
  fetchStatusCountsWithFilters,
} from '../well-summary/WellSummaryDashboardSlice';

// Import widget components
import AlarmStatusWidget from './AlarmStatusWidget';
import WellStatusWidget from './WellStatusWidget';
import FieldProduction from './components/FieldProduction';
import ClassificationSummary from './components/ClassificationSummary/ClassificationSummary';
import { apiService } from '../../services/wellSummaryDashboard/WellSummaryService';
import MenuNavIcon from '../navigation/components/NavMenuIcon';
// import ProductionMetricsWidget from './components/ProductionMetricsWidget';
// import TaskOverviewWidget from './components/TaskOverviewWidget';

const GridLayout = WidthProvider(RGL);

// Interface for widget data
interface WidgetData {
  key: string;
  layout: {
    lg: {
      x: number;
      y: number;
      width: number;
      height: number;
      minH?: number;
      minW?: number;
    };
  };
  widgetProperty: {
    moduleId?: number;
    title?: string;
    description?: string;
  };
}

// Interface for grid layout items
interface GridLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minH?: number;
  minW?: number;
  moduleId?: number;
}

// Duplicate local type used in WellFilterSlice for stronger typing (keeps change local/minimal)
type SectionFilter = { section: string; value: string };

// Placeholder widget component
const PlaceholderWidget: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <div className='placeholder-widget h-full w-full bg-gray-100 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center p-6'>
    <div className='text-center'>
      <div className='widget-icon w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-lg mx-auto mb-4 flex items-center justify-center'>
        <svg className='w-8 h-8 text-gray-500 dark:text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
          />
        </svg>
      </div>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2'>{title}</h3>
      <p className='text-sm text-gray-600 dark:text-gray-400'>{description}</p>
    </div>
  </div>
);

// Mock widget layout data - this would typically come from an API or Redux store
const defaultWidgetLayout: WidgetData[] = [
  {
    key: 'wellSummary',
    layout: {
      lg: { x: 0, y: 0, width: 7.5, height: 8.8, minH: 8.8, minW: 4 },
    },
    widgetProperty: {
      moduleId: 1,
      title: 'Well Summary',
      description: 'Overview of well information and current status',
    },
  },
  {
    key: 'alarmStatus',
    layout: {
      lg: { x: 7.5, y: 0, width: 1.5, height: 8.8, minH: 8.8, minW: 2 },
    },
    widgetProperty: {
      moduleId: 2,
      title: 'Alarm Status',
      description: 'Current alarms and system notifications',
    },
  },
  {
    key: 'classificationSummary',
    layout: {
      lg: { x: 9, y: 0, width: 3, height: 8.8, minH: 8.8, minW: 3 },
    },
    widgetProperty: {
      moduleId: 3,
      title: 'Rod Lift Classification summary',
      description: 'Rod Lift Classification summary',
    },
  },
  {
    key: 'performanceChart',
    layout: {
      lg: { x: 0, y: 12, width: 12, height: 15, minH: 10, minW: 8 },
    },
    widgetProperty: {
      moduleId: 4,
      title: 'Field production (By MOP)',
      description: 'Historical performance trends and analytics',
    },
  },
];

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return width;
};

const SummaryDashboard: React.FC = () => {
  // Extracted remove filter handler
  const handleRemoveFilter = (filterToRemove: SectionFilter | 'ALL') => {
    // Remove the filter from the selectedFilters array
    const currentFilters: SectionFilter[] = Array.isArray(selectedFilters) ? [...selectedFilters] : [];
    const updatedFilters: SectionFilter[] =
      filterToRemove === 'ALL'
        ? []
        : currentFilters.filter((f) => !(f.section === filterToRemove.section && f.value === filterToRemove.value));

    // Helper to convert filters array to DashboardFilterArr (as in WellFilterPanel)
    const arrToCleanArr = (arr: unknown[] = []) =>
      (arr || [])
        .map((item) => {
          if (item == null) return '';
          if (typeof item === 'string') return item.trim();
          if (typeof item === 'object') {
            const obj = item as { displayName?: unknown; label?: unknown; name?: unknown; value?: unknown };
            const val = obj.displayName ?? obj.label ?? obj.name ?? obj.value ?? '';
            return typeof val === 'string' ? val.trim() : String(val).trim();
          }
          return '';
        })
        .filter((v): v is string => Boolean(v));

    const targetFilters = updatedFilters;
    const businessUnits = arrToCleanArr(targetFilters.filter((f) => f.section === 'Business unit').map((f) => f.value));
    const fields = arrToCleanArr(targetFilters.filter((f) => f.section === 'Field').map((f) => f.value));
    const mops = arrToCleanArr(targetFilters.filter((f) => f.section === 'Method of production').map((f) => f.value));
    const groups = arrToCleanArr(targetFilters.filter((f) => f.section === 'Groups').map((f) => f.value));

    const payloadArr = {
      mops: mops.length ? mops : undefined,
      businessUnits: businessUnits.length ? businessUnits : undefined,
      fields: fields.length ? fields : undefined,
      groups: groups.length ? groups : undefined,
    };
    const payload = {
      mops: mops.length ? mops.join(',') : undefined,
      businessUnits: businessUnits.length ? businessUnits.join(',') : undefined,
      fields: fields.length ? fields.join(',') : undefined,
      groups: groups.length ? groups.join(',') : undefined,
    };

    // BEFORE dispatch(removeFilter(...))
    apiService.clearAllCaches();

    // Remove the filter in Redux
    dispatch(removeFilter(filterToRemove));
    // Fetch updated data for widgets
    dispatch(fetchAlarmsData(payloadArr));
    dispatch(fetchRodpumpclassification(payloadArr));
    dispatch(fetchFieldProduction(payload));

    // AFTER fetchFieldProduction(...)
    dispatch(fetchFilteredWellSummary(payload)); // /WellSummary with CSV filters
    ['Active', 'Running', 'Shutdown', 'CommFail', 'Offline', 'Cycling', 'NoData', 'Restarted'].forEach((status) => {
      dispatch(fetchStatusCountsWithFilters({ status, filters: payload })); // /WellDataSummary + /WellSummary per status
    });
  };
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // Mock Redux state selectors - replace with actual selectors when available
  const summaryPageData: WidgetData[] = defaultWidgetLayout;
  const windowWidth = useWindowWidth();

  // selected filters (used to keep filter chips in header and to toggle spacing)
  const selectedFilters = useAppSelector(
    (state: { WellFiltersSlice?: { selectedFilters?: SectionFilter[] } }) =>
      state.WellFiltersSlice?.selectedFilters || [],
  );

  useEffect(() => {
    const fetchData = () => {
      try {
        // Fetch dashboard configuration data
        // await dispatch(fetchColumnsData('summary-dashboard'));
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch summary dashboard data:', error);
        setErrorMessage('Failed to load dashboard data');
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Convert widget layout data to grid layout format
  const safeLayout = (widgetData: WidgetData[]): Layout[] => {
    return widgetData
      ?.map((obj: WidgetData, index: number): GridLayoutItem | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          console.warn(`Invalid layout at index ${index}`, obj);
          return null;
        }

        // Responsive adjustment
        let w = lg.width;
        let x = lg.x;
        let y = lg.y;
        if (windowWidth <= 800) {
          if (obj.key === 'wellSummary') {
            w = 12;
            x = 0;
          }
          if (obj.key === 'alarmStatus') {
            w = 12;
            x = 0;
          }
          if (obj.key === 'classificationSummary') {
            w = 12;
            x = 0;
          }
        } else if (windowWidth <= 1024) {
          if (obj.key === 'wellSummary') {
            w = 12;
            x = 0;
            y = 0;
          }
          if (obj.key === 'alarmStatus') {
            w = 6;
            x = 0;
            y = 1;
          }
          if (obj.key === 'classificationSummary') {
            w = 6;
            x = 6;
            y = 2;
          }
        } else if (windowWidth <= 1440) {
          if (obj.key === 'wellSummary') {
            w = 10;
            x = 0;
            y = 0;
          }
          if (obj.key === 'alarmStatus') {
            w = 2;
            x = 10;
            y = 0;
          }
          if (obj.key === 'classificationSummary') {
            w = 12;
            x = 0;
            y = 1;
          }
        }

        const item: GridLayoutItem = {
          i: String(obj.key),
          x,
          y,
          w,
          h: lg.height,
          minH: lg.minH ?? 6,
          minW: lg.minW ?? 3,
          moduleId: obj.widgetProperty?.moduleId,
        };

        return item;
      })
      .filter((item): item is GridLayoutItem => item !== null);
  };

  const layoutMemo = useMemo(() => safeLayout(summaryPageData), [summaryPageData, windowWidth]);

  // Render widget content based on widget key
  const renderWidgetContent = (widgetKey: string, widgetData: WidgetData | undefined) => {
    switch (widgetKey) {
      case 'wellSummary':
        return <WellStatusWidget />;
      case 'classificationSummary':
        return <ClassificationSummary />;
      case 'alarmStatus':
        return <AlarmStatusWidget />;
      case 'taskOverview':
        return (
          <PlaceholderWidget
            title={widgetData?.widgetProperty?.title || 'Task Overview'}
            description={widgetData?.widgetProperty?.description || 'Pending tasks and schedules'}
          />
        );
      case 'recentActivity':
        return (
          <PlaceholderWidget
            title={widgetData?.widgetProperty?.title || 'Recent Activity'}
            description={widgetData?.widgetProperty?.description || 'Latest events and changes'}
          />
        );
      case 'performanceChart':
        return <FieldProduction />;
      default:
        return <PlaceholderWidget title='Unknown Widget' description='Widget content not implemented yet' />;
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  // determine if there are selected filters so SCSS can add spacing if needed
  const hasSelectedFilters = Array.isArray(selectedFilters) && selectedFilters.length > 0;

  return (
    <ToastServiceProvider>
      {/* add has-selected-filters class when chips are present so SCSS can add small gap if necessary */}
      <div className={`summary-dashboard ${hasSelectedFilters ? 'has-selected-filters' : ''}`}>
        {errorMessage && (
          <div className='error-message bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
            {errorMessage}
          </div>
        )}

        {/* Header container: title row + header controls */}
        <div className='summary-header-container'>
          <div className='group-status-top-row'>
            <div className='nav-menu-icon-header-container'>
              <MenuNavIcon />
              <span className='group-status-group-name'>Summary dashboard</span>
            </div>
            <div className='custom-views-header-elements'>
              <HeaderElements showFilterIcon={true} />
            </div>
          </div>

          {/* Filters slot (keeps chips in-flow beneath the title row) */}
        </div>
        <div className='summary-header-filters-section'>
          <SelectedWellFilters
            filters={selectedFilters}
            onRemoveFilter={handleRemoveFilter}
            recommendationsPage={false}
          />
        </div>
        <GridLayout
          layout={layoutMemo}
          cols={12}
          rowHeight={30}
          isDraggable={false}
          isResizable={false}
          className='layout-react-grid'
          useCSSTransforms={false}
          isDroppable={false}
          margin={[24, 24]}
          containerPadding={[24, 24]}
        >
          {layoutMemo?.map((item: Layout) => {
            const widgetData = summaryPageData.find((widget: WidgetData) => widget.key === item.i);
            return (
              <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                {renderWidgetContent(item.i, widgetData)}
              </div>
            );
          })}
        </GridLayout>
      </div>
    </ToastServiceProvider>
  );
};

export default SummaryDashboard;
